/**
 * Push-notification toast copy (HATDEV-100044 / Activate feedback).
 *
 * CDN/Firestore ngx-translate bundles may omit these keys, so callers should
 * resolve via resolvePushToastLanguage + pushNotificationToastLine.
 */

export type PushToastKey =
  | "subscribed"
  | "notifications_on"
  | "something_went_wrong"
  | "push_unavailable_private"
  | "add_to_home_screen_push"
  | "install_app_to_activate";

export const PUSH_NOTIFICATION_TOAST_I18N: Record<
  PushToastKey,
  Record<string, string>
> = {
  subscribed: {
    en: "Subscribed!",
    de: "Abonniert!",
    es: "¡Suscrito!",
    fr: "Abonné !",
    it: "Iscritto!",
  },
  notifications_on: {
    en: "Notifications on",
    de: "Benachrichtigungen aktiv",
    es: "Notificaciones activadas",
    fr: "Notifications activées",
    it: "Notifiche attive",
  },
  something_went_wrong: {
    en: "Something went wrong",
    de: "Etwas ist schiefgelaufen",
    es: "Algo salió mal",
    fr: "Une erreur s'est produite",
    it: "Qualcosa è andato storto",
  },
  push_unavailable_private: {
    en: "Push notifications aren't available in private browsing",
    de: "Push-Benachrichtigungen sind im privaten Surfen nicht verfügbar",
    es: "Las notificaciones push no están disponibles en la navegación privada",
    fr: "Les notifications push ne sont pas disponibles en navigation privée",
    it: "Le notifiche push non sono disponibili in navigazione privata",
  },
  add_to_home_screen_push: {
    en: "Add the app to your home screen to receive push notifications and stay updated.",
    de: "Fügen Sie die App zum Home-Bildschirm hinzu, um Push-Benachrichtigungen zu erhalten und auf dem Laufenden zu bleiben.",
    es: "Añade la app a tu pantalla de inicio para recibir notificaciones push y mantenerte al día.",
    fr: "Ajoutez l'application à votre écran d'accueil pour recevoir des notifications push et rester informé.",
    it: "Aggiungi l'app alla schermata Home per ricevere le notifiche push e restare aggiornato.",
  },
  install_app_to_activate: {
    en: "Please install the app to activate notifications",
    de: "Bitte installieren Sie die App, um Benachrichtigungen zu aktivieren",
    es: "Instala la app para activar las notificaciones",
    fr: "Veuillez installer l'application pour activer les notifications",
    it: "Installa l'app per attivare le notifiche",
  },
};

export function resolvePushToastLanguage(
  authLang?: string | null,
  storageLang?: string | null,
  translateLang?: string | null
): string {
  const raw = (authLang || storageLang || translateLang || "en")
    .toString()
    .trim()
    .toLowerCase();
  return raw.split("-")[0] || "en";
}

export function readLanguageCodeFromStorage(): string {
  try {
    if (typeof localStorage === "undefined" || localStorage === null) {
      return "";
    }
    return localStorage.getItem("language_code") || "";
  } catch {
    return "";
  }
}

export function pushNotificationToastLine(
  key: PushToastKey,
  lang: string,
  ngxTranslated?: string | null
): string {
  const ngxKey = `vouchers.${key}`;
  if (
    typeof ngxTranslated === "string" &&
    ngxTranslated.trim() &&
    ngxTranslated !== ngxKey
  ) {
    return ngxTranslated;
  }
  const code = (lang || "en").toLowerCase().split("-")[0] || "en";
  return (
    PUSH_NOTIFICATION_TOAST_I18N[key]?.[code] ||
    PUSH_NOTIFICATION_TOAST_I18N[key]?.en ||
    ""
  );
}

/** Normalise getConstantsMsg payload so MyToastbar always gets { line_1, line_2 }. */
export function normalizePushConstantsToast(
  translatedMsg: {
    message?: unknown;
    style?: Record<string, unknown>;
  } | null | undefined,
  defaultMsg: {
    message: { line_1: string; line_2?: string };
    style: Record<string, unknown>;
  }
): {
  message: { line_1: string; line_2?: string };
  style: Record<string, unknown>;
} {
  if (!translatedMsg) {
    return defaultMsg;
  }
  const msg = translatedMsg.message;
  if (msg && typeof msg === "object" && (msg as { line_1?: unknown }).line_1 != null) {
    return {
      message: msg as { line_1: string; line_2?: string },
      style: { ...defaultMsg.style, ...(translatedMsg.style || {}) },
    };
  }
  if (typeof msg === "string" && msg.trim()) {
    return {
      message: { line_1: msg, line_2: "" },
      style: { ...defaultMsg.style, ...(translatedMsg.style || {}) },
    };
  }
  return defaultMsg;
}

export function buildPushToastPayload(
  key: PushToastKey,
  lang: string,
  style: Record<string, unknown>,
  ngxTranslated?: string | null
): {
  message: { line_1: string; line_2: string };
  style: Record<string, unknown>;
} {
  return {
    message: {
      line_1: pushNotificationToastLine(key, lang, ngxTranslated),
      line_2: "",
    },
    style,
  };
}

/** Banner body copy keys (CDN ngx-translate may omit these on Live). */
export type PushBannerCopyKey =
  | "enable_push"
  | "claim_free_lines"
  | "never_miss"
  | "unlock_offer"
  | "activate";

export const PUSH_BANNER_COPY_I18N: Record<
  PushBannerCopyKey,
  Record<string, string>
> = {
  enable_push: {
    en: "Enable Push Notifications",
    de: "Push-Benachrichtigungen aktivieren",
    es: "Activar notificaciones push",
    fr: "Activer les notifications push",
    it: "Attiva le notifiche push",
  },
  claim_free_lines: {
    en: "Claim 2 FREE Powerball lines",
    de: "2 GRATIS Powerball-Zeilen beanspruchen",
    es: "Reclama 2 líneas GRATIS de Powerball",
    fr: "Réclamez 2 lignes Powerball GRATUITES",
    it: "Richiedi 2 linee Powerball GRATIS",
  },
  never_miss: {
    en: "Never miss a draw",
    de: "Keine Ziehung verpassen",
    es: "No te pierdas ningún sorteo",
    fr: "Ne manquez jamais un tirage",
    it: "Non perdere mai un'estrazione",
  },
  unlock_offer: {
    en: "Unlock exclusive offers and rewards",
    de: "Exklusive Angebote und Belohnungen freischalten",
    es: "Desbloquea ofertas y recompensas exclusivas",
    fr: "Débloquez des offres et récompenses exclusives",
    it: "Sblocca offerte e premi esclusivi",
  },
  activate: {
    en: "Activate",
    de: "Aktivieren",
    es: "Activar",
    fr: "Activer",
    it: "Attiva",
  },
};

export function pushBannerCopyLine(
  key: PushBannerCopyKey,
  lang: string,
  ngxTranslated?: string | null
): string {
  const ngxKey = `vouchers.${key}`;
  if (
    typeof ngxTranslated === "string" &&
    ngxTranslated.trim() &&
    ngxTranslated !== ngxKey
  ) {
    return ngxTranslated;
  }
  const code = (lang || "en").toLowerCase().split("-")[0] || "en";
  return (
    PUSH_BANNER_COPY_I18N[key]?.[code] ||
    PUSH_BANNER_COPY_I18N[key]?.en ||
    ""
  );
}

/**
 * Localised banner content when Firestore has no content[lang] block.
 * Avoids Live CDN ngx-translate falling back to English for missing vouchers.* keys.
 */
export function buildLocalPushBannerContent(lang: string): {
  title: string;
  line_1: string;
  line_1_alt: string;
  line_2: string;
  button_label: string;
} {
  const code = resolvePushToastLanguage(lang, null, null);
  return {
    title: pushBannerCopyLine("enable_push", code),
    line_1: pushBannerCopyLine("claim_free_lines", code),
    line_1_alt: pushBannerCopyLine("never_miss", code),
    line_2: pushBannerCopyLine("unlock_offer", code),
    button_label: pushBannerCopyLine("activate", code),
  };
}
